<template>
  <section class="ine-form">
    <Background />
    <LoginForm
      :isLoading="isLoading"
      :isAuthenticated="isAuthenticated"
      :authProviders="authProviders"
      :recaptchaSitekey="recaptchaSitekey"
      :showResendEmail="showResendEmail"
      @resend-loading="isResendLoading"
      @submit-form="fetchRecaptcha"
      @submit-company="loginWithFederated"
      @login-through-provider="loginThroughProvider"
    />
    <div
      class="
          ine-form__captcha-wrapper"
    >
      <vue-recaptcha
        ref="invisibleRecaptcha"
        :sitekey="recaptchaSitekey"
        recaptchaHost="www.recaptcha.net"
        @verify="onCaptchaVerify"
        @expired="onCaptchaExpire"
        size="invisible"
        :loadRecaptchaScript="true"
      />
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import Background from '@/components/forms/elements/Background.vue'
import LoginForm from '@/components/forms/LoginForm.vue'
import VueRecaptcha from 'vue-recaptcha'
import CONSTANTS from '@/constants'

const createDOMPurify = require('dompurify')

const DOMPurify = createDOMPurify(window)

export default {
  name: 'Login',
  metaInfo() {
    return {
      title: `INE - Login`,
      meta: [
        {
          vmid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  },
  components: {
    Background,
    LoginForm,
    VueRecaptcha,
  },
  data() {
    return {
      isLoading: false,
      showResendEmail: false,
      from: null,
      recaptcha: '',
      email: '',
      password: '',
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.from = from
    })
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      isTwoFactorRequired: state => state.auth.isTwoFactorRequired,
      isPageLoading: state => state.app.isPageLoading,
      searchQuery: state => state.search.searchQuery,
      authProviders: state => state.auth.authProviders,
      redirectUrl: state => state.auth.redirectUrl,
      onboardingSurvey: state => state.auth.onboardingSurvey,
    }),
    recaptchaSitekey() {
      return process.env.VUE_APP_RECAPTCHA_SITEKEY
    },
    isOnboardingSurveyEnabled() {
      return process.env.VUE_APP_ONBOARDING_SURVEY
    },
  },
  methods: {
    onCaptchaVerify(token) {
      this.recaptcha = token
      this.submitForm()
    },
    onCaptchaExpire() {
      this.recaptcha = ''
      this.$refs.invisibleRecaptcha.reset()
    },
    fetchRecaptcha({ email, password }) {
      this.isLoading = true
      this.email = email
      this.password = password
      this.$refs.invisibleRecaptcha.execute()
    },
    async submitForm() {
      const { redirectUrl, email, password, recaptcha } = this
      try {
        const payload = { email, password, recaptcha }
        const response = await this.$store.dispatch('auth/login', payload)
        if (
          response?.code === CONSTANTS.KNOWN_API_ERRORS.CODES.USER_NOT_CONFIRMED
        )
          this.showResendEmail = true
        const { isTwoFactorRequired, isAuthenticated } = this
        if (isTwoFactorRequired) {
          this.$router.push({ name: 'two-factor' })
          return
        }

        if (isAuthenticated) {
          if (
            this.onboardingSurvey?.firstAttempt &&
            this.isOnboardingSurveyEnabled
          ) {
            this.$store.dispatch('app/setOnboardingModalStatus', {
              opened: true,
              step: CONSTANTS.PROFILE.ONBOARDING.STEPS.PROFILE_PHOTO,
            })
          }
          this.sendMixpanelEvent(this.eventsConstants.ACCOUNT_USER_LOGIN, {})
          if (!redirectUrl) {
            this.getNextRoute()
          } else {
            window.location.assign(redirectUrl)
          }
        }
      } catch (error) {
        this.onCaptchaExpire()
        this.isLoading = false
      } finally {
        this.onCaptchaExpire()
        this.isLoading = false
      }
    },
    loginThroughProvider(provider) {
      this.isLoading = true
      const { url } = provider
      this.$store.dispatch('auth/saveRedirectUrl')
      window.open(url, '_parent')
    },
    isResendLoading(value) {
      this.isLoading = value
      if (!value) this.showResendEmail = false
    },
    getNextRoute() {
      const goHomeRoutes = [
        'confirm-account',
        'reset-password',
        'update-password',
        'home',
      ]
      if (
        this.from &&
        this.from.name &&
        goHomeRoutes.includes(this.from.name)
      ) {
        this.$router.push({ name: 'home' })
      } else {
        if (this.from && this.from.fullPath) {
          this.$router.push(this.from.fullPath)
        } else {
          this.$router.push({ name: 'home' })
        }
      }
    },
    checkForParams(params) {
      if (params.has('error')) {
        this.$store.dispatch(
          'app/setError',
          DOMPurify.sanitize(params.get('error'), {
            ALLOWED_TAGS: [],
          })
        )
      }
      if (params.has('redirect_url')) {
        const redirectUrl = this.buildRedirectUrl(params)
        this.$store.dispatch('auth/setRedirectUrl', redirectUrl)
      }
    },
    buildRedirectUrl(params) {
      params.delete('error')
      let buildString = params.get('redirect_url')
      params.delete('redirect_url')
      const entries = [...params.entries()]
      if (entries && entries.length) {
        const allParams = entries.reduce((acc, [key, value]) => {
          if (Array.isArray(value)) {
            acc.push(`${key}=${value.join()}`)
          } else {
            acc.push(`${key}=${value}`)
          }
          return acc
        }, [])
        buildString += `?${allParams.join('&')}`
      }
      return buildString
    },
    async loginWithFederated(company) {
      try {
        this.isLoading = true
        const response = await this.$store.dispatch(
          'auth/getFederatedUrl',
          company
        )
        if (response.login_url) {
          window.location.assign(response.login_url)
        }
      } catch (error) {
        this.isLoading = false
      }
    },
  },
  async created() {
    if (this.isAuthenticated) {
      return this.$router.push({ name: 'home' })
    }
    if (this.isTwoFactorRequired) {
      this.$router.push({ name: 'two-factor' })
    } else {
      this.$store.dispatch('auth/setRedirectUrl', '')
      this.checkForParams(new URLSearchParams(window.location.search))
    }
  },
}
</script>
